import {
  getListTemplatesQueryKey,
  useCreateTemplate,
  useDeleteTemplate,
  useListTemplates,
  useUpdateTemplate,
} from "@/api/generated/hooks/admin/admin";
import type { Template } from "@/api/generated/types";
import { PageHeader } from "@/components/features/common/page-header";
import { ContentContainer } from "@/components/features/layout/content-container";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { unwrapBody } from "@/lib/api-response";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useMemo, useState } from "react";

interface TemplateFormState {
  slug: string;
  title: string;
  description: string;
  introPrompt: string;
  durationMinutes: string;
  questionPlan: string;
  communication: string;
  problemSolving: string;
  domainKnowledge: string;
  confidence: string;
}

const emptyForm: TemplateFormState = {
  slug: "",
  title: "",
  description: "",
  introPrompt: "",
  durationMinutes: "30",
  questionPlan: "",
  communication: "25",
  problemSolving: "25",
  domainKnowledge: "25",
  confidence: "25",
};

function toFormState(template?: Template | null): TemplateFormState {
  if (!template) {
    return emptyForm;
  }

  return {
    slug: template.slug,
    title: template.title,
    description: template.description,
    introPrompt: template.introPrompt,
    durationMinutes: String(template.durationMinutes),
    questionPlan: template.questionPlan.join("\n"),
    communication: String(template.rubric.communication ?? 25),
    problemSolving: String(template.rubric.problemSolving ?? 25),
    domainKnowledge: String(template.rubric.domainKnowledge ?? 25),
    confidence: String(template.rubric.confidence ?? 25),
  };
}

export function TemplatesPage() {
  const queryClient = useQueryClient();
  const templatesQuery = useListTemplates({
    query: {
      select: (response) => unwrapBody(response).templates,
    },
  });
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();
  const deleteMutation = useDeleteTemplate();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [form, setForm] = useState<TemplateFormState>(emptyForm);

  const selectedTemplate = useMemo(
    () => templatesQuery.data?.find((template) => template.id === selectedTemplateId) ?? null,
    [selectedTemplateId, templatesQuery.data]
  );

  function updateField(field: keyof TemplateFormState, value: string) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function resetForm(template?: Template | null) {
    const nextTemplate = template ?? null;
    setSelectedTemplateId(nextTemplate?.id ?? null);
    setForm(toFormState(nextTemplate));
  }

  async function refreshTemplates() {
    await queryClient.invalidateQueries({ queryKey: getListTemplatesQueryKey() });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const payload = {
      slug: form.slug,
      title: form.title,
      description: form.description,
      introPrompt: form.introPrompt,
      durationMinutes: Number(form.durationMinutes),
      questionPlan: form.questionPlan
        .split("\n")
        .map((value) => value.trim())
        .filter((value) => value.length > 0),
      rubric: {
        communication: Number(form.communication),
        problemSolving: Number(form.problemSolving),
        domainKnowledge: Number(form.domainKnowledge),
        confidence: Number(form.confidence),
      },
      isActive: true,
    };

    if (selectedTemplateId) {
      await updateMutation.mutateAsync({
        id: selectedTemplateId,
        data: payload,
      });
    } else {
      await createMutation.mutateAsync({
        data: payload,
      });
    }

    await refreshTemplates();
    resetForm();
  }

  async function handleDelete(templateId: string) {
    await deleteMutation.mutateAsync({ id: templateId });
    await refreshTemplates();
    if (selectedTemplateId === templateId) {
      resetForm();
    }
  }

  return (
    <main className="flex-1 overflow-y-auto bg-background">
      <ContentContainer className="space-y-8">
        <PageHeader
          title="面談テンプレート"
          breadcrumbs={[{ label: "面談テンプレート" }]}
          subtitle="質問計画と評価ルーブリックを管理します。"
        />

        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <Card>
            <CardHeader>
              <CardTitle>テンプレート一覧</CardTitle>
              <CardDescription>面談作成に使うテンプレートを確認します。</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {templatesQuery.isLoading && (
                <p className="text-sm text-muted-foreground">読み込み中...</p>
              )}
              {templatesQuery.data?.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => resetForm(template)}
                  className="w-full rounded-xl border border-slate-200 p-4 text-left transition hover:border-primary hover:bg-slate-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-slate-900">{template.title}</p>
                      <p className="text-xs text-slate-500">{template.slug}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleDelete(template.id);
                      }}
                    >
                      削除
                    </Button>
                  </div>
                  <p className="mt-3 text-sm text-slate-600">{template.description}</p>
                </button>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{selectedTemplate ? "テンプレート編集" : "テンプレート作成"}</CardTitle>
              <CardDescription>UI から面談テンプレートを更新できます。</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleSubmit}>
                <Input
                  value={form.slug}
                  onChange={(event) => updateField("slug", event.target.value)}
                  placeholder="slug"
                />
                <Input
                  value={form.title}
                  onChange={(event) => updateField("title", event.target.value)}
                  placeholder="タイトル"
                />
                <Textarea
                  value={form.description}
                  onChange={(event) => updateField("description", event.target.value)}
                  placeholder="説明"
                />
                <Textarea
                  value={form.introPrompt}
                  onChange={(event) => updateField("introPrompt", event.target.value)}
                  placeholder="導入プロンプト"
                />
                <Input
                  value={form.durationMinutes}
                  onChange={(event) => updateField("durationMinutes", event.target.value)}
                  type="number"
                  min={5}
                  max={45}
                  placeholder="面談時間"
                />
                <Textarea
                  value={form.questionPlan}
                  onChange={(event) => updateField("questionPlan", event.target.value)}
                  placeholder={"質問を1行ずつ入力してください"}
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    value={form.communication}
                    onChange={(event) => updateField("communication", event.target.value)}
                    type="number"
                    placeholder="communication"
                  />
                  <Input
                    value={form.problemSolving}
                    onChange={(event) => updateField("problemSolving", event.target.value)}
                    type="number"
                    placeholder="problemSolving"
                  />
                  <Input
                    value={form.domainKnowledge}
                    onChange={(event) => updateField("domainKnowledge", event.target.value)}
                    type="number"
                    placeholder="domainKnowledge"
                  />
                  <Input
                    value={form.confidence}
                    onChange={(event) => updateField("confidence", event.target.value)}
                    type="number"
                    placeholder="confidence"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    {selectedTemplate ? "更新する" : "作成する"}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => resetForm()}>
                    リセット
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </ContentContainer>
    </main>
  );
}
